'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { ArrowDownUp, CircleCheckBig, CircleHelp, CirclePlus, CircleX, LogIn, Medal, MessageCircleX, PenLine, SendHorizonal, Star, Trash, X } from 'lucide-react'
import { Button } from '@/components/elements/Button'
import TextEditorMininal from '@/components/elements/TextEditorMinimal'
import EmptyState from '@/components/elements/EmptyState'
import ImageCarousel from '@/components/blocks/ImageCarousel'
import { useCart, useProductReviews, type ReviewFilterId } from '@/lib/commerce/hooks'
import { classNames, formatImageURL, formatNumber, getVendorPrimaryColor, isLoyaltyActive } from '@/utils/util'
import { navigateUrl, sendClientRequest } from '@/utils/client-request'

interface ReviewProps {
  product: any
  vendor: any
  reviews: {
    counts: { rating: number; count: number }[]
    data: any[]
    totalCount: number
    average: number
    userReviews?: any[]
  }
  canReview?: boolean
}

const INITIAL_VISIBLE = 3

function relativeTime(date: Date) {
  const now = new Date()
  const d = new Date(date)
  const seconds = Math.floor((now.getTime() - d.getTime()) / 1000)
  if (seconds < 60) return 'Дөнгөж сая'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} минутын өмнө`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} цагийн өмнө`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days} өдрийн өмнө`
  const weeks = Math.floor(days / 7)
  if (weeks < 5) return `${weeks} долоо хоногийн өмнө`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months} сарын өмнө`
  const years = Math.floor(days / 365)
  return `${years} жилийн өмнө`
}

export default function Review({ product, vendor, reviews: initialReviews, canReview = false }: ReviewProps) {
  const pathname = usePathname()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const primaryColor = getVendorPrimaryColor(vendor)
  const { loyaltySummary } = useCart()
  const [publicBasePoints, setPublicBasePoints] = useState(0)
  const reviewRewardPoints = isLoyaltyActive(vendor) ? (loyaltySummary?.currentTier?.reviewRewardPoints || 0) : 0
  const reviewRewardCapCount = loyaltySummary?.reviewRewardCapCount || 0
  const reviewRewardCapWindowDays = loyaltySummary?.reviewRewardCapWindowDays || 0
  const reviewRewardCapped = reviewRewardPoints > 0 && reviewRewardCapCount > 0 && reviewRewardCapWindowDays > 0 && (loyaltySummary?.reviewRewardUsedCount || 0) >= reviewRewardCapCount

  useEffect(() => {
    if (canReview || !isLoyaltyActive(vendor)) return
    let cancelled = false
    sendClientRequest({ url: '/loyalty/tiers', auth: false, cache: true, headers: { Vendor: vendor?.id } }).then(({ data }: any) => {
      if (cancelled || !data?.tiers?.length) return
      const sorted = [...data.tiers].sort((a: any, b: any) => a.minAnnualSpend - b.minAnnualSpend)
      setPublicBasePoints(sorted[0]?.reviewRewardPoints || 0)
    }).catch(() => { })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReview, vendor?.id])

  const reviewPointsChip = reviewRewardPoints > 0 && (
    reviewRewardCapped ? (
      <span className="inline-flex items-center gap-1 bg-zinc-100 text-zinc-500 text-[11.5px] font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap tabular-nums">
        <Medal className="size-3.5" />
        Онооны лимит хүрсэн
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 bg-violet-100 text-violet-700 text-[11.5px] font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap tabular-nums">
        <Medal className="size-3.5" />
        +{formatNumber(reviewRewardPoints)} оноо
      </span>
    )
  )

  const {
    reviews,
    deletedReview, pendingReview, publishedReview, hasAnyReview,
    totalCount, photoCount, fiveStarCount,
    getFilteredReviews,
    content, setContent,
    rating, setRating,
    images,
    btnLoading,
    loadingImages,
    postReview,
    uploadImages,
    removeImage,
  } = useProductReviews({ product, initialReviews })

  const [isCarouselOpen, setIsCarouselOpen] = useState(false)
  const [carouselImages, setCarouselImages] = useState<string[]>([])
  const [activeImage, setActiveImage] = useState<string>('')
  const [activeFilter, setActiveFilter] = useState<ReviewFilterId>('all')
  const [composerOpen, setComposerOpen] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const [sortNewest, setSortNewest] = useState(true)

  const filteredReviews = useMemo(() => getFilteredReviews(activeFilter, sortNewest), [reviews.data, activeFilter, sortNewest])
  const visibleReviews = showAll ? filteredReviews : filteredReviews.slice(0, INITIAL_VISIBLE)
  const hasMore = filteredReviews.length > INITIAL_VISIBLE

  if (!product) return <></>

  const average = reviews.average ? Number(reviews.average) : 0
  const averageText = average ? average.toFixed(1) : '0.0'

  const handleImages = async (e: any) => {
    await uploadImages(e.target.files)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const openImageInCarousel = (imgs: string[], clickedImage: string) => {
    setCarouselImages(imgs)
    setActiveImage(clickedImage)
    setIsCarouselOpen(true)
  }

  const filters: { id: ReviewFilterId, label: string }[] = [
    { id: 'all', label: `Бүгд (${totalCount})` },
    { id: '5', label: `5★ (${fiveStarCount})` },
    { id: 'photo', label: `Зурагтай (${photoCount})` },
  ]

  const composer = (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {[0, 1, 2, 3, 4].map((r) => (
            <Star key={r} onClick={() => setRating(r + 1)} className={classNames(rating > r ? 'text-yellow-400 fill-yellow-400' : 'fill-foreground/10 text-foreground/10', 'size-6 shrink-0 cursor-pointer')} />
          ))}
        </div>
        <input type="file" accept="image/*" multiple ref={fileInputRef} className="hidden" onChange={handleImages} />
        <Button variant="outline" icon={CirclePlus} isLoading={loadingImages} onClick={() => fileInputRef.current?.click()}>
          Зураг
        </Button>
      </div>
      <div className="py-3 px-4 border border-foreground/10 rounded-2xl min-h-32 [&_.ProseMirror]:min-h-24 [&_.ProseMirror]:outline-none bg-cream">
        <TextEditorMininal content={content} setContent={setContent} />
      </div>
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {images.map((image, index) => (
            <div key={index} className="relative aspect-square">
              <img src={formatImageURL(image)} alt={`Review image ${index + 1}`} className="w-full h-full object-cover rounded-xl bg-foreground/5 cursor-pointer" onClick={() => openImageInCarousel(images, image)} />
              <button type="button" onClick={() => removeImage(image)} className="absolute top-1 right-1 h-6 w-6 grid place-items-center rounded-full bg-black/60 text-white hover:bg-black/80">
                <Trash className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-end">
        <Button icon={SendHorizonal} isLoading={btnLoading} onClick={postReview} color={primaryColor}>
          Илгээх
        </Button>
      </div>
    </div>
  )

  const composerSection = canReview ? (
    hasAnyReview ? (
      <div className="mt-4 sm:mt-8 bg-white rounded-3xl border border-foreground/5 p-5 sm:p-6">
        <UserReviewStatus publishedReview={publishedReview} pendingReview={pendingReview} deletedReview={deletedReview} composerOpen={composerOpen} setComposerOpen={setComposerOpen} composer={composer} openImageInCarousel={openImageInCarousel} />
      </div>
    ) : composerOpen ? (
      <div className="mt-4 sm:mt-8 bg-white rounded-3xl border border-foreground/5 p-5 sm:p-6">
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-2">
            <div className="text-base font-bold font-gilroy text-foreground tracking-[-0.01em]">Сэтгэгдэл бичих</div>
            {reviewPointsChip}
          </div>
          <button type="button" onClick={() => setComposerOpen(false)} className="h-8 w-8 grid place-items-center rounded-full text-foreground/55 hover:bg-foreground/5">
            <X className="h-4 w-4" />
          </button>
        </div>
        {composer}
      </div>
    ) : (
      <div className="mt-4 sm:mt-8 bg-white rounded-3xl border border-foreground/5 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-[16px] font-bold font-gilroy text-foreground tracking-[-0.01em]">Энэ бүтээгдэхүүний талаар сэтгэгдэл үлдээх үү?</h3>
            {reviewPointsChip}
          </div>
          <p className="text-[13.5px] text-foreground/60 leading-relaxed mt-1">
            {reviewRewardCapped
              ? `Та ${reviewRewardCapWindowDays} хоногт ${reviewRewardCapCount} сэтгэгдэл бичиж оноо цуглуулах лимитэд хүрсэн байна. Таны энэ сэтгэгдэлд оноо олгогдохгүй ч бусад худалдан авагчдад худалдан авалт хийхэд нь туслаарай.`
              : reviewRewardPoints > 0
                ? `Үнэлгээ өгч бусад худалдан авагчдад туслаад, ${formatNumber(reviewRewardPoints)} оноо цуглуулаарай.`
                : 'Хэрэглэсэн бүтээгдэхүүнийхээ талаар үнэлгээ өгч, бусад худалдан авагчдад туслаарай.'}
          </p>
        </div>
        <Button icon={PenLine} onClick={() => setComposerOpen(true)} color={primaryColor}>
          Сэтгэгдэл бичих
        </Button>
      </div>
    )
  ) : (
    <div className="mt-4 sm:mt-8 flex flex-col items-center gap-2 text-[13.5px] text-foreground/60">
      {publicBasePoints > 0 && (
        <span className="inline-flex items-center gap-1 bg-violet-100 text-violet-700 text-[11.5px] font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap tabular-nums">
          <Medal className="size-3.5" />
          +{formatNumber(publicBasePoints)} оноо
        </span>
      )}
      <div className="flex items-center justify-center gap-1.5 flex-wrap text-center">
        <span>{publicBasePoints > 0 ? `Нэвтэрч сэтгэгдэл, үнэлгээ үлдээгээд ${formatNumber(publicBasePoints)} оноо цуглуулахын тулд` : 'Сэтгэгдэл, үнэлгээ үлдээхийн тулд'}</span>
        <a href={navigateUrl(`/login?url=/product/${product.customId}`, pathname)} className="inline-flex items-center gap-1 font-semibold text-foreground hover:underline">
          <LogIn className="size-3.5" /> нэвтэрнэ үү
        </a>
      </div>
    </div>
  )

  return (
    <div className="font-inter">
      <ImageCarousel images={carouselImages} initialImage={activeImage} isOpen={isCarouselOpen} onClose={() => setIsCarouselOpen(false)} productName={product.name} vendor={vendor} />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <aside className="lg:col-span-4 self-start">
          <h2 className="text-[28px] sm:text-[32px] font-bold font-gilroy tracking-[-0.02em] leading-none text-foreground">
            Сэтгэгдэл
          </h2>
          <p className="mt-3 text-[14px] text-foreground/60 leading-relaxed">
            Худалдан авагчид энэ барааны талаар
          </p>

          <div className="mt-8 flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-[#FFF4CC] inline-flex items-center justify-center shrink-0">
              <Star className="size-5 fill-foreground text-foreground" />
            </div>
            <div className="min-w-0">
              <div className="text-[40px] font-bold font-gilroy leading-none tracking-[-0.02em] text-foreground tabular-nums">
                {averageText}
              </div>
              <div className="mt-1.5 text-[13px] text-foreground/60">
                <span className="font-semibold text-foreground tabular-nums">{totalCount.toLocaleString('en-US')}</span> үнэлгээтэй
              </div>
            </div>
          </div>

          <div className="mt-6 space-y-2.5 max-w-sm">
            {[5, 4, 3, 2, 1].map((star) => {
              const c = reviews.counts.find((x) => x.rating === star)
              const count = c?.count ?? 0
              const pct = totalCount > 0 ? Math.round((count / totalCount) * 100) : 0
              return (
                <div key={star} className="grid grid-cols-[12px_1fr_64px] items-center gap-3 text-[12.5px]">
                  <span className="text-foreground/70 tabular-nums">{star}</span>
                  <div className="h-0.75 rounded-full bg-foreground/8 overflow-hidden">
                    <div className="h-full rounded-full bg-foreground transition-all" style={{ width: `${Math.max(pct, count > 0 ? 4 : 0)}%` }} />
                  </div>
                  <div className="text-right tabular-nums whitespace-nowrap">
                    <span className="font-semibold text-foreground">{pct}%</span>
                    <span className="ml-1 text-[11px] text-foreground/50">({count})</span>
                  </div>
                </div>
              )
            })}
          </div>
        </aside>

        <div className="lg:col-span-8">
          {(filteredReviews.length > 0 || activeFilter !== 'all') && (
            <div className="flex items-center gap-2 flex-wrap mb-6">
              <div className="flex items-center gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {filters.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => { setActiveFilter(f.id); setShowAll(false) }}
                    className={classNames(
                      'shrink-0 h-8 px-3.5 rounded-full text-[12.5px] font-semibold transition-colors cursor-pointer',
                      activeFilter === f.id ? 'bg-foreground text-cream' : 'bg-transparent border border-foreground/10 text-foreground/70 hover:border-foreground/40'
                    )}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setSortNewest((s) => !s)}
                className="ml-auto shrink-0 inline-flex items-center gap-1.5 text-[12px] text-foreground/60 hover:text-foreground transition-colors cursor-pointer"
              >
                <ArrowDownUp className="size-3.5" />
                <span>{sortNewest ? 'Сүүлийнх' : 'Эхнийх'}</span>
              </button>
            </div>
          )}

          {filteredReviews.length > 0 ? (
            <>
              <div className="divide-y divide-foreground/10">
                {visibleReviews.map((rev: any) => (
                  <ReviewItem key={rev.id} rev={rev} openImageInCarousel={openImageInCarousel} />
                ))}
              </div>

              {hasMore && (
                <div className="mt-8 flex justify-center">
                  <button
                    onClick={() => setShowAll((s) => !s)}
                    className="h-11 px-6 rounded-full bg-foreground/5 text-foreground text-[13.5px] font-semibold hover:bg-foreground/10 transition-colors cursor-pointer"
                  >
                    {showAll ? 'Багасгах' : 'Бүх сэтгэгдэл харах'}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div>
              <EmptyState
                icon={MessageCircleX}
                title={activeFilter === 'all' ? 'Сэтгэгдэл хоосон байна' : 'Тохирох сэтгэгдэл олдсонгүй'}
                description={activeFilter === 'all' ? 'Яг одоогоор ямар нэгэн сэтгэгдэл байхгүй байна.' : 'Өөр шүүлтүүр сонгож үзнэ үү.'}
              />
            </div>
          )}
        </div>
      </div>

      {composerSection}
    </div>
  )
}

function StarRow({ value, size = 14 }: { value: number, size?: number }) {
  return (
    <div className="inline-flex items-center gap-0.5 text-foreground">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star key={i} className={classNames(i < Math.round(value || 0) ? 'fill-foreground text-foreground' : 'fill-foreground/15 text-foreground/15')} style={{ width: size, height: size }} />
      ))}
    </div>
  )
}

const AVATAR_TINTS = ['#EEEAFF', '#FFE0F0', '#E1F0FF', '#E6F4EA', '#FFF4CC', '#FFE7D6']
function avatarTint(name: string) {
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0
  return AVATAR_TINTS[h % AVATAR_TINTS.length]
}

function ReviewItem({ rev, openImageInCarousel }: { rev: any, openImageInCarousel: (imgs: string[], clicked: string) => void }) {
  const [expanded, setExpanded] = useState(false)
  const name = rev.customer?.firstName || 'Хэрэглэгч'
  const truncate = !expanded && rev.comment && rev.comment.replace(/<[^>]*>/g, '').length > 280
  const tint = avatarTint(rev.customer?.id || name)

  return (
    <article className="py-6 first:pt-0 last:pb-0">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {rev.customer?.avatar ? (
            <img src={formatImageURL(rev.customer.avatar)} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
          ) : (
            <div className="w-10 h-10 rounded-full inline-flex items-center justify-center font-bold text-foreground shrink-0" style={{ background: tint }}>
              {name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-[14px] font-semibold text-foreground truncate">{name}</div>
            <div className="text-[12px] text-foreground/55">{relativeTime(new Date(rev.createdAt))}</div>
          </div>
        </div>
        <StarRow value={rev.rating} />
      </div>

      {rev.comment ? (
        <div className="mt-3 text-[14px] leading-relaxed text-foreground/80">
          <div
            className={classNames('blog-detail prose max-w-none prose-p:mt-0 prose-p:mb-0', truncate && 'line-clamp-3')}
            dangerouslySetInnerHTML={{ __html: rev.comment }}
          />
          {(truncate || expanded) && (
            <button
              onClick={() => setExpanded((e) => !e)}
              className="mt-1 text-[13.5px] font-semibold text-foreground underline underline-offset-4 decoration-foreground/40 hover:decoration-foreground transition-colors cursor-pointer"
            >
              {expanded ? 'Багасгах' : 'Дэлгэрэнгүй'}
            </button>
          )}
        </div>
      ) : (
        <div className="mt-3 text-[14px] italic text-foreground/45">Сэтгэгдэл хоосон байна</div>
      )}
      {rev.images && rev.images.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {rev.images.map((image: any, idx: number) => (
            <img key={idx} src={formatImageURL(image)} alt={`Review image ${idx + 1}`} className="w-16 h-16 object-cover rounded-xl cursor-pointer" onClick={() => openImageInCarousel(rev.images || [], image)} />
          ))}
        </div>
      )}
    </article>
  )
}

function UserReviewStatus({ publishedReview, pendingReview, deletedReview, composerOpen, setComposerOpen, composer, openImageInCarousel }: { publishedReview: any, pendingReview: any, deletedReview: any, composerOpen: boolean, setComposerOpen: (v: boolean) => void, composer: React.ReactNode, openImageInCarousel: (imgs: string[], clicked: string) => void }) {
  return (
    <div className="space-y-3">
      {publishedReview && (
        <StatusBlock
          tone="emerald"
          icon={CircleCheckBig}
          title="Та амжилттай үнэлгээ өгсөн байна"
          badge="Нийтлэгдсэн"
          review={publishedReview}
          openImageInCarousel={openImageInCarousel}
        />
      )}
      {pendingReview && (
        <StatusBlock
          tone="amber"
          icon={CircleHelp}
          title="Таны үнэлгээ хянагдаж байна"
          badge="Хянагдаж байна"
          review={pendingReview}
          openImageInCarousel={openImageInCarousel}
        />
      )}
      {deletedReview && !publishedReview && (
        <div>
          <StatusBlock
            tone="rose"
            icon={CircleX}
            title="Таны үнэлгээг татгалзсан байна"
            badge="Татгалзсан"
            review={deletedReview}
            openImageInCarousel={openImageInCarousel}
            note={pendingReview ? 'Засварлагдсан сэтгэгдлийг хянаж байна.' : 'Та сэтгэгдлээ дахин засварлаж илгээх боломжтой.'}
          />
          {!pendingReview && !publishedReview && (
            composerOpen ? (
              <div className="mt-4 pt-4 border-t border-foreground/10">
                {composer}
              </div>
            ) : (
              <div className="mt-3 flex justify-end">
                <Button variant="outline" icon={PenLine} onClick={() => setComposerOpen(true)}>
                  Дахин үнэлгээ өгөх
                </Button>
              </div>
            )
          )}
        </div>
      )}
    </div>
  )
}

const TONE: Record<string, { iconBg: string, iconColor: string, badgeBg: string, badgeText: string }> = {
  emerald: { iconBg: 'bg-emerald-50', iconColor: 'text-emerald-600', badgeBg: 'bg-emerald-50', badgeText: 'text-emerald-700' },
  amber: { iconBg: 'bg-amber-50', iconColor: 'text-amber-600', badgeBg: 'bg-amber-50', badgeText: 'text-amber-700' },
  rose: { iconBg: 'bg-rose-50', iconColor: 'text-rose-600', badgeBg: 'bg-rose-50', badgeText: 'text-rose-700' },
}

function StatusBlock({ tone, icon: Icon, title, badge, review, openImageInCarousel, note }: { tone: string, icon: any, title: string, badge: string, review: any, openImageInCarousel: (imgs: string[], clicked: string) => void, note?: string }) {
  const t = TONE[tone]
  const [expanded, setExpanded] = useState(false)
  const plainLength = review.comment ? review.comment.replace(/<[^>]*>/g, '').length : 0
  const truncate = !expanded && plainLength > 180
  return (
    <div>
      <div className="flex items-start gap-4">
        <div className={classNames('h-11 w-11 grid place-items-center rounded-2xl shrink-0', t.iconBg)}>
          <Icon className={classNames('size-5.5', t.iconColor)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[18px] font-bold font-gilroy text-foreground tracking-[-0.015em] leading-tight">{title}</div>
            <span className={classNames('inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full', t.badgeBg, t.badgeText)}>
              {badge}
            </span>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <div className="flex items-center gap-0.5">
              {[0, 1, 2, 3, 4].map((r) => (
                <Star key={r} className={classNames(review.rating > r ? 'text-yellow-400 fill-yellow-400' : 'fill-foreground/10 text-foreground/10', 'size-3.5')} />
              ))}
            </div>
            {review.createdAt && (
              <span className="text-[12px] text-foreground/55">· {relativeTime(new Date(review.createdAt))}</span>
            )}
          </div>
          {note && <p className="text-[12.5px] mt-1.5 text-foreground/60 leading-snug">{note}</p>}
        </div>
      </div>
      {review.comment && (
        <div className="mt-3 text-[13.5px] leading-relaxed text-foreground/75">
          <div
            className={classNames('blog-detail prose max-w-none prose-p:mt-0 prose-p:mb-0', truncate && 'line-clamp-3')}
            dangerouslySetInnerHTML={{ __html: review.comment }}
          />
          {(truncate || expanded) && (
            <button
              onClick={() => setExpanded((e) => !e)}
              className="mt-1 text-[13px] font-semibold text-foreground underline underline-offset-4 decoration-foreground/40 hover:decoration-foreground transition-colors cursor-pointer"
            >
              {expanded ? 'Багасгах' : 'Дэлгэрэнгүй'}
            </button>
          )}
        </div>
      )}
      {review.images && review.images.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {review.images.map((image: any, idx: number) => (
            <img key={idx} src={formatImageURL(image)} alt={`Review image ${idx + 1}`} className="w-16 h-16 object-cover rounded-xl cursor-pointer" onClick={() => openImageInCarousel(review.images || [], image)} />
          ))}
        </div>
      )}
    </div>
  )
}
